import React, {useContext, useEffect} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

// Material UI
import {Grid} from "@mui/material";

// Components
import BottomDrawer from "components/WMS/Mobile/BottomDrawer";
import CustomButton from "components/CustomButton";
import SearchBar from "components/List/components/SearchBar";

// API
import {getWmsItemsByRetailer} from "api/wms/wmsItems/wmsItemsByRetailer";

// Contexts
import {WMSContext} from "../../../context/WMSContext";
import {MissionContext} from "../../context/MissionContext";

// Utils
import RetailerToAddLine from "./components/RetailerToAddLine";
import AddRetailerButton from "./components/AddRetailerButton";
import ActionContainer from "./components/ActionContainer";

AddRetailers.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
};

function AddRetailers({open, handleClose}) {
    const {currentInventory} = useContext(WMSContext);

    const {
        retailerNewWmsItems,
        setRetailerNewWmsItems,
        selectedRetailers,
        setSelectedRetailers,
        setRetailersToBeAddedToMission
    } = useContext(MissionContext);

    const [searchString, setSearchString] = React.useState('');

    useEffect(() => {
        if (!open) {
            return;
        }

        if (retailerNewWmsItems === null) {
            getWmsItemsByRetailer(currentInventory.id).then((response) => {
                setRetailerNewWmsItems(response);
                initializeSelectedRetailers(response);
            });
        }
    }, [retailerNewWmsItems, open]);

    if (!open) {
        return null;
    }

    function initializeSelectedRetailers(retailers) {
        setSelectedRetailers(Object.fromEntries(
            Object.keys(retailers).map(id => [id, false])
        ));
    }

    function checkRetailer(retailerId) {
        setSelectedRetailers(prevState => ({
            ...prevState,
            [retailerId]: !prevState[retailerId]
        }));
    }

    function hasNoSelectedRetailers() {
        return Object.values(selectedRetailers).every(value => !value);
    }

    function resetSelectedRetailers() {
        setSelectedRetailers(Object.fromEntries(
            Object.keys(selectedRetailers).map(id => [id, false])
        ));

        setSearchString('');
    }

    return (
        <BottomDrawer
            open={open}
            onClose={handleClose}
            title={<FormattedMessage id='wms.myPickingMissions.add.retailer.title' />}
            subtitle={<FormattedMessage id='wms.myPickingMissions.add.retailer.subtitle' />}
            disablePortal={true}
            openMaxHeight
            noHeightLimit
        >
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                height: '100%',
                overflow: 'auto',
            }}>
                <div>
                    <div
                        style={{
                            background: '#fff',
                            padding: '16px',
                        }}
                    >
                        <SearchBar
                            customPlaceholder='wms.myPickingMissions.add.retailer.search.placeholder'
                            onChange={(value) => setSearchString(value)}
                            iconToTheRight={true}
                        />
                    </div>
                    {retailerNewWmsItems !== null && Object.entries(retailerNewWmsItems)
                        .filter(([id, value]) => value.name.includes(searchString))
                        .map(([retailerId, value]) => (
                            <RetailerToAddLine
                                key={retailerId}
                                onItemClick={() => checkRetailer(retailerId)}
                                checked={selectedRetailers[retailerId] || false}
                                id={retailerId}
                                name={value.name}
                                count={value.count}
                                logo={value.logo}
                            />
                        ))
                    }
                </div>
                <ActionContainer>
                    <Grid item>
                        <CustomButton
                            disabled={hasNoSelectedRetailers()}
                            onClick={() => resetSelectedRetailers()}
                            thicker={true}
                            color={'default'}
                        >
                            <FormattedMessage id='wms.myPickingMissions.add.retailer.reset' />
                        </CustomButton>
                    </Grid>
                    <Grid item xs>
                        <AddRetailerButton
                            selectedRetailers={selectedRetailers}
                            onClick={() => {
                                handleClose();
                                setRetailersToBeAddedToMission(
                                    Object.entries(selectedRetailers)
                                        .filter(([id, isChecked]) => isChecked === true)
                                        .map(([id, isChecked]) => id)
                                )
                            }}
                        />
                    </Grid>
                </ActionContainer>
            </div>
        </BottomDrawer>
    );
}

export default AddRetailers;
